import { handleCartItemVariantsRenderDebounced } from "../components/CartItemVariantsInfo/CartItemVariantsRenderer";
import {
    BundleConfig,
    BBTierOptions,
    FixedBoxConfig,
    PlaceholderStepsType,
    StepConditionType,
    StepsType,
} from "./Bundles/Types";
import { CartConfigInterface } from "./Cart/CartTypes";
import { CART_CHANGE_EVENT, CART_CHANGE_FETCH_EVENT, CART_SHOPS_VARIANTS_INFO, injectCSS } from "./constants";

export function hexToRgba(hex: any = "#000000", alpha: any = 1) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function hsbaToHex(h: number, s: number, b: number, a: number = 1): string {
    // Convert percentages to decimals
    s /= 100;
    b /= 100;

    const c = b * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = b - c;

    let r = 0,
        g = 0,
        b2 = 0;

    if (h >= 0 && h < 60) {
        r = c;
        g = x;
        b2 = 0;
    } else if (h >= 60 && h < 120) {
        r = x;
        g = c;
        b2 = 0;
    } else if (h >= 120 && h < 180) {
        r = 0;
        g = c;
        b2 = x;
    } else if (h >= 180 && h < 240) {
        r = 0;
        g = x;
        b2 = c;
    } else if (h >= 240 && h < 300) {
        r = x;
        g = 0;
        b2 = c;
    } else if (h >= 300 && h < 360) {
        r = c;
        g = 0;
        b2 = x;
    }

    // Convert to 8-bit (0-255) range
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b2 = Math.round((b2 + m) * 255);
    const alpha = Math.round(a * 255);

    // Convert to HEX format
    const hex = (n: number) => n.toString(16).padStart(2, "0");

    return a < 1 ? `#${hex(r)}${hex(g)}${hex(b2)}${hex(alpha)}` : `#${hex(r)}${hex(g)}${hex(b2)}`;
}

export function hexToHsba(hex: string): { hue: number; saturation: number; brightness: number; alpha: number } {
    // Remove the '#' if present
    hex = hex?.replace(/^#/, "");

    let r,
        g,
        b,
        a = 1;

    if (hex?.length === 3) {
        // Handle shorthand HEX (#RGB)
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
    } else if (hex?.length === 4) {
        // Handle shorthand HEX with Alpha (#RGBA)
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
        a = parseInt(hex[3] + hex[3], 16) / 255;
    } else if (hex?.length === 6) {
        // Handle full HEX (#RRGGBB)
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
    } else if (hex?.length === 8) {
        // Handle full HEX with Alpha (#RRGGBBAA)
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
        a = parseInt(hex.slice(6, 8), 16) / 255;
    } else {
        // throw new Error("Invalid HEX color format");
        console.warn("Invalid HEX color format");
    }

    // Convert RGB (0-255) to normalized range (0-1)
    const rNorm = (r as number) / 255;
    const gNorm = (g as number) / 255;
    const bNorm = (b as number) / 255;

    // Get min and max RGB values
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;

    // Calculate Hue
    let h = 0;
    if (delta !== 0) {
        if (max === rNorm) {
            h = ((gNorm - bNorm) / delta) % 6;
        } else if (max === gNorm) {
            h = (bNorm - rNorm) / delta + 2;
        } else {
            h = (rNorm - gNorm) / delta + 4;
        }
        h = h * 60;
        if (h < 0) h += 360;
    }

    // Calculate Brightness (Value)
    const brightness = max; // Already normalized between 0 and 1

    // Calculate Saturation
    const saturation = max === 0 ? 0 : delta / max;

    return {
        hue: parseFloat(h.toFixed(6)), // Hue with 6 decimal precision
        saturation: parseFloat(saturation.toFixed(6)), // Saturation between 0 and 1
        brightness: parseFloat(brightness.toFixed(6)), // Brightness between 0 and 1
        alpha: parseFloat(a.toFixed(6)), // Alpha between 0 and 1
    };
}

export const currencyToSymbols: any = {
    AED: "د.إ",
    AFN: "؋",
    ALL: "L",
    AMD: "֏",
    ANG: "ƒ",
    AOA: "Kz",
    ARS: "$",
    AUD: "$",
    AWG: "ƒ",
    AZN: "₼",
    BAM: "KM",
    BBD: "$",
    BDT: "৳",
    BGN: "лв",
    BHD: ".د.ب",
    BIF: "FBu",
    BMD: "$",
    BND: "$",
    BOB: "Bs.",
    BRL: "R$",
    BSD: "$",
    BTN: "Nu.",
    BWP: "P",
    BYN: "Br",
    BZD: "$",
    CAD: "$",
    CDF: "FC",
    CHF: "Fr.",
    CLP: "$",
    CNY: "¥",
    COP: "$",
    CRC: "₡",
    CUP: "$",
    CVE: "$",
    CZK: "Kč",
    DJF: "Fdj",
    DKK: "kr",
    DOP: "$",
    DZD: "د.ج",
    EGP: "£",
    ERN: "Nfk",
    ETB: "Br",
    EUR: "€",
    FJD: "$",
    FKP: "£",
    FOK: "kr",
    GBP: "£",
    GEL: "₾",
    GGP: "£",
    GHS: "₵",
    GIP: "£",
    GMD: "D",
    GNF: "FG",
    GTQ: "Q",
    GYD: "$",
    HKD: "$",
    HNL: "L",
    HRK: "kn",
    HTG: "G",
    HUF: "Ft",
    IDR: "Rp",
    ILS: "₪",
    IMP: "£",
    INR: "₹",
    IQD: "ع.د",
    IRR: "﷼",
    ISK: "kr",
    JEP: "£",
    JMD: "$",
    JOD: "د.ا",
    JPY: "¥",
    KES: "Sh",
    KGS: "лв",
    KHR: "៛",
    KID: "$",
    KMF: "CF",
    KRW: "₩",
    KWD: "د.ك",
    KYD: "$",
    KZT: "₸",
    LAK: "₭",
    LBP: "ل.ل",
    LKR: "Rs",
    LRD: "$",
    LSL: "L",
    LYD: "ل.د",
    MAD: "د.م.",
    MDL: "L",
    MGA: "Ar",
    MKD: "ден",
    MMK: "K",
    MNT: "₮",
    MOP: "P",
    MRU: "UM",
    MUR: "₨",
    MVR: "Rf",
    MWK: "MK",
    MXN: "$",
    MYR: "RM",
    MZN: "MT",
    NAD: "$",
    NGN: "₦",
    NIO: "C$",
    NOK: "kr",
    NPR: "₨",
    NZD: "$",
    OMR: "ر.ع.",
    PAB: "B/.",
    PEN: "S/",
    PGK: "K",
    PHP: "₱",
    PKR: "₨",
    PLN: "zł",
    PYG: "₲",
    QAR: "ر.ق",
    RON: "lei",
    RSD: "дин",
    RUB: "₽",
    RWF: "FRw",
    SAR: "﷼",
    SBD: "$",
    SCR: "₨",
    SDG: "ج.س.",
    SEK: "kr",
    SGD: "$",
    SHP: "£",
    SLE: "Le",
    SLL: "Le",
    SOS: "Sh",
    SRD: "$",
    SSP: "£",
    STN: "Db",
    SYP: "£",
    SZL: "L",
    THB: "฿",
    TJS: "ЅМ",
    TMT: "T",
    TND: "د.ت",
    TOP: "T$",
    TRY: "₺",
    TTD: "$",
    TVD: "$",
    TWD: "NT$",
    TZS: "Sh",
    UAH: "₴",
    UGX: "Sh",
    USD: "$",
    UYU: "$U",
    UZS: "сўм",
    VES: "Bs.",
    VND: "₫",
    VUV: "Vt",
    WST: "T",
    XAF: "FCFA",
    XCD: "$",
    XDR: "SDR",
    XOF: "CFA",
    XPF: "₣",
    YER: "﷼",
    ZAR: "R",
    ZMW: "ZK",
    ZWL: "$",
};
export type TCurrencyCode = keyof typeof currencyToSymbols;

export const convertCurrency = (value: number, fromCurrency: TCurrencyCode, toCurrency: TCurrencyCode) => {
    const rates = window.kefi_currency_rates;

    if (!rates) {
        throw new Error("Currency rates not available");
    }
    // @ts-expect-error required
    if (!rates[fromCurrency] || !rates[toCurrency]) {
        throw new Error("Invalid currency specified");
    }

    // @ts-expect-error required
    const fromRate = rates[fromCurrency];
    // @ts-expect-error required
    const toRate = rates[toCurrency];

    // Perform the conversion
    const convertedValue = (value * toRate) / fromRate;

    return convertedValue?.toFixed(2);
};

export const overrideFetch = () => {
    const originalFetch = window.fetch;
    window.originalFetch = originalFetch;

    window.fetch = async function (input, options) {
        const isInstanceOfRequest = input instanceof Request;
        const isInstanceOfURL = input instanceof URL;
        const clone: any = isInstanceOfRequest && input.clone();

        const response = await originalFetch(input, options);
        try {
            console._log("Custom fetch :", input, options);

            let body: any = null;
            let url: string = "";

            if (isInstanceOfRequest) {
                url = input.url;
                body = await clone.text();
            } else if (isInstanceOfURL) {
                url = input.toString();
                body = options?.body || null;
            } else {
                url = input;
                body = options?.body || null;
            }

            if (url.includes("?silent")) {
                return response;
            }

            // ---- dispatch cart events---- only dispatch for these endpoints
            const shouldDispatchCartEvent = /\/cart\/(change|update)\.js$|\/cart\.js$/.test(url);

            if (shouldDispatchCartEvent) {
                let cartData: any = null;
                try {
                    cartData = await response.clone().json();
                } catch (err) {
                    console._log?.("Failed to parse cart response JSON in overrideFetch:", err);
                }

                // Dispatch event only for these endpoints
                document.dispatchEvent(new CustomEvent(CART_CHANGE_FETCH_EVENT, { detail: { cartData, url } }));
            }

            const cartChangeRequest = /\/cart\/(add|change|update|clear)/.test(url);

            if (cartChangeRequest) {
                const isCartLinesUpdating = url.includes("cart/update") ? isUpdatingCartLines(body) : true;

                document.dispatchEvent(
                    new CustomEvent(CART_CHANGE_EVENT, { detail: { url, body, isCartLinesUpdating } }),
                );

                if (!isCartLinesUpdating) {
                    console._log("Skipping cart update for non-line item changes:", url);
                    return response;
                }

                if (!window.kefi_bundles.discount_legacy_flow) {
                    let timeoutId: any = null;
                    const isCartAnalyzed = new Promise((res: any) => {
                        window.__cartAnalyzedResolve = res;
                        timeoutId = setTimeout(() => {
                            res();
                            console._log("after timeout executed!!!", url);
                        }, 5000);
                    });

                    await isCartAnalyzed;
                    clearTimeout(timeoutId);
                }

                // If kefi_cart_available is true, we don't need to fetch sections
                if (window.kefi_cart_available) return response;

                let sections: any = null;

                try {
                    if (body) {
                        if (typeof body == "string") {
                            const JSONbody = JSON.parse(body);
                            if (JSONbody.sections) sections = JSONbody.sections;
                        } else if (body.get) {
                            const _sections = body.get("sections");
                            if (_sections) sections = _sections;
                        }
                    }
                } catch (error) {
                    console._log("error in sections");
                }

                if (!sections) {
                    const cart: any =
                        document.querySelector("cart-drawer") || document.querySelector("cart-notification");

                    if (cart && cart.getSectionsToRender) {
                        sections = cart.getSectionsToRender().map((s: any) => s.section || s.id || s.selector);
                    }
                }

                if (sections) {
                    try {
                        const res = await fetch(
                            `/?sections=${Array.isArray(sections) ? sections.join(",") : sections}`,
                        );

                        const updatedSections = await res.json();

                        const _response = new Response(
                            JSON.stringify({
                                ...(await response.json()),
                                sections: updatedSections,
                            }),
                            {
                                status: response.status, // Optional: Set HTTP status code
                                headers: response.headers, // Optional: Set headers
                            },
                        );
                        return _response;
                    } catch (error) {
                        console._log("Error fetching sections:", error);
                    }
                }
            }
        } catch (error) {
            console.error("Error in fetch override:", error);
        }

        return response;
    };
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method: string, url: string) {
        (this as any)._method = method;
        (this as any)._url = url;
        // eslint-disable-next-line prefer-rest-params
        return originalXHROpen.apply(this, arguments as any);
    };

    XMLHttpRequest.prototype.send = async function (body: any) {
        const xhr = this as any;
        const url = xhr._url || "";
        // eslint-disable-next-line prefer-rest-params
        originalXHRSend.apply(this, arguments as any);

        const cartChangeRequest = /\/cart\/(add|change|update)/.test(url);
        if (cartChangeRequest) {
            const originalOnLoad = xhr.onload;

            xhr.onload = async function (...args: any) {
                const isCartLinesUpdating = url.includes("cart/update") ? isUpdatingCartLines(body) : true;

                document.dispatchEvent(
                    new CustomEvent(CART_CHANGE_EVENT, { detail: { url, body, isCartLinesUpdating } }),
                );

                if (!isCartLinesUpdating) {
                    console._log("Skipping cart update for non-line item changes:", url);
                } else {
                    document.dispatchEvent(new CustomEvent(CART_CHANGE_EVENT, { detail: { url, body } }));
                    if (!window.kefi_bundles.discount_legacy_flow) {
                        let timeoutId: any = null;
                        const isCartAnalyzed = new Promise((res: any) => {
                            window.__cartAnalyzedResolve = res;
                            timeoutId = setTimeout(() => {
                                res();
                                console._log("after timeout executed!!!:XHR", url);
                            }, 5000);
                        });

                        await isCartAnalyzed;
                        clearTimeout(timeoutId);
                    }
                }
                if (originalOnLoad) {
                    originalOnLoad.apply(xhr, args);
                }
            };
        }
    };
};

export const hideLineItemPrivateProperties = () => {
    try {
        document.querySelectorAll("[data-cart-item-property]").forEach((item) => {
            const label = item.querySelector("[data-cart-item-property-name]");
            if (label?.textContent?.trim()?.startsWith("_")) {
                // @ts-expect-error hack
                item.style.display = "none";
            }
        });
    } catch (error) {
        console._log(error);
    }
};

export function mergeConfig(cartConfig: CartConfigInterface, apiConfig?: CartConfigInterface): CartConfigInterface {
    const apiData = apiConfig || {};

    return Object.keys({ ...cartConfig, ...apiData }).reduce((acc, key) => {
        const cartValue = cartConfig[key as keyof CartConfigInterface];
        // @ts-expect-error hack
        const apiValue = apiData[key as keyof CartConfigInterface];

        if (
            typeof cartValue === "object" &&
            cartValue !== null &&
            !Array.isArray(cartValue) &&
            typeof apiValue === "object" &&
            apiValue !== null
        ) {
            // @ts-expect-error hack
            acc[key as keyof CartConfigInterface] = { ...cartValue, ...apiValue };
        } else if (key == "cart_features_positions" && Array.isArray(cartValue) && Array.isArray(apiValue)) {
            // Ensure the order follows apiValue
            // @ts-expect-error required
            const cartMap = new Map(cartValue.map((item) => [item.key, item]));

            const mergedArray = apiValue.map((item) => cartMap.get(item.key) || item);

            // Append any cartConfig items not in apiValue
            cartValue.forEach((item) => {
                // @ts-expect-error required
                if (!mergedArray.some((apiItem) => apiItem.key === item.key)) {
                    mergedArray.push(item);
                }
            });
            // @ts-expect-error hack
            acc[key as keyof CartConfigInterface] = mergedArray;
        } else {
            // @ts-expect-error hack
            acc[key as keyof CartConfigInterface] = apiValue !== undefined ? apiValue : cartValue;
        }

        return acc;
    }, {} as CartConfigInterface);
}

const isPlainObject = (val: unknown): val is Record<string, any> =>
    !!val && typeof val === "object" && !Array.isArray(val);

export function deepMergeWithDefaults<T>(defaults: T, incoming: any): T {
    if (incoming === undefined) return defaults;
    if (incoming === null) return incoming as T;

    if (Array.isArray(defaults) && Array.isArray(incoming)) {
        const template = defaults[0];
        return incoming.map((item) => deepMergeWithDefaults(template, item)) as any;
    }

    if (isPlainObject(defaults) && isPlainObject(incoming)) {
        const result: Record<string, any> = { ...defaults };
        for (const key of Object.keys(incoming)) {
            result[key] =
                key in defaults ? deepMergeWithDefaults((defaults as any)[key], incoming[key]) : incoming[key];
        }
        return result as T;
    }

    return incoming as T;
}

export function hideShopifyCartElements() {
    const style = document.createElement("style");
    style.id = "cart-style"; // Add an ID to prevent duplicate styles
    const isEvvvgg = window?.kefi_bundles?.shop === "evvvgg-b1.myshopify.com";
    style.textContent = `
    .halo-cart-sidebar,
    .background-overlay,
     cart-popup,
     cart,
     cart-drawer,
    .cart-popup,
     ${isEvvvgg ? ".cart-drawer > dialog" : ".cart-drawer"},
    .cart-notification,
    .yv_side_drawer_wrapper,
    #mini-cart-drawer,
    #modals-rightDrawer,
    .side_Drawer_open:after {
            display: none !important;
        }
    `;

    if (window.Shopify?.theme?.schema_name === "Be Yours") {
        style.textContent = `
            .halo-cart-sidebar,
    .background-overlay,
     cart-popup,
     cart,
    .cart-popup,
     ${isEvvvgg ? ".cart-drawer > dialog" : ".cart-drawer"},
    .cart-notification,
    .yv_side_drawer_wrapper,
    #mini-cart-drawer,
    #modals-rightDrawer,
    .side_Drawer_open:after {
            display: none !important;
        }
    `;
    }

    if (window.Shopify?.theme?.schema_name === "Impact") {
        style.textContent = `
            .halo-cart-sidebar,
            .background-overlay,
            .cart-popup,
            .cart-drawer,
            .cart-notification,
            .yv_side_drawer_wrapper,
            #mini-cart-drawer,
            #modals-rightDrawer,
            .side_Drawer_open:after {
                    display: none !important;
                }
        `;
    }
    if (window.Shopify?.theme?.schema_name === "Horizon") {
        style.textContent = `
            .halo-cart-sidebar,
            .background-overlay,
            .cart-popup,
            .cart-notification,
            .yv_side_drawer_wrapper,
            #mini-cart-drawer,
            #modals-rightDrawer,
            .side_Drawer_open:after {
                    display: none !important;
                }
        `;
    }
    if (window.Shopify?.theme?.schema_name === "Motion") {
        style.textContent = `
            .halo-cart-sidebar,
            .background-overlay,
            .cart-popup,
            .cart-notification,
            .yv_side_drawer_wrapper,
            #mini-cart-drawer,
            #CartDrawer,
            #modals-rightDrawer,
            .side_Drawer_open:after {
                    display: none !important;
                }
        `;
    }
    if (window.Shopify?.theme?.schema_name === "Shapes") {
        style.textContent = `
            .halo-cart-sidebar,
            .background-overlay,
            .cart-popup,
            .cart-notification,
            .yv_side_drawer_wrapper,
            #mini-cart-drawer,
            #CartDrawer,
            .side_Drawer_open:after {
                    display: none !important;
                }
        `;
    }

    if (!document.getElementById("cart-style")) {
        document.head.appendChild(style);
    }
}

export function addDefaultCartToProductForm() {
    const productForms: any = document.getElementsByTagName("product-form") || [];
    if (productForms?.length) {
        Array?.from(productForms)?.forEach((pf: any) => {
            if (!pf?.cart) {
                if (!document.querySelector("cart-notification-dummy")) {
                    const cartNotification = document.createElement("cart-notification-dummy");

                    document.body.appendChild(cartNotification);
                }
                pf.cart = document.querySelector("cart-notification-dummy");
            }
        });
    }
}

export function handleCustomCheckout() {
    if (window.gokwikSdk) {
        try {
            //GoKwik Checkout
            window.gokwikSdk.initCheckout(window.merchantInfo);
        } catch (error) {
            window.gokwikSdk = null;
            handleCustomCheckout();
            console.error("GoKwik checkout failed", error);
        }
    } else if (window.openRzpCheckout) {
        try {
            // Razorpay Checkout
            window.openRzpCheckout();
        } catch (error) {
            window.openRzpCheckout = null;
            handleCustomCheckout();
            console.error("Razorpay checkout failed", error);
        }
    } else if (window.Shopflo) {
        try {
            //Shopflo Checkout
            window.Shopflo.openCheckout();
        } catch (error) {
            window.Shopflo = null;
            handleCustomCheckout();
            console.error("Shopflo checkout failed", error);
        }
    } else if (window.zecpeAutomatedThemeSetupDone) {
        try {
            //360 checkout Checkout
            window.LoadOccScript(true);
        } catch (error) {
            window.zecpeAutomatedThemeSetupDone = false;
            handleCustomCheckout();
            console.error("360  checkout failed", error);
        }
    } else if (window.CashfreeOcc) {
        try {
            //Cashfree Checkout
            const checkoutBtn = document.getElementById("kefiCheckoutButton");
            if (checkoutBtn) {
                window.CashfreeOcc.initCheckout(checkoutBtn);
            }
        } catch (error) {
            window.CashfreeOcc = null;
            handleCustomCheckout();
            console.error("Cashfree checkout failed", error);
        }
    } else if (window.kefi_bundles.shop == "laenia.myshopify.com") {
        try {
            const checkoutBtn = document.getElementById("kefiCheckoutButton");
            if (checkoutBtn) {
                const cartModal: any = document.querySelector(".mw-btn-modal");
                cartModal?.click();
            } else {
                window.location = "/checkout/" as unknown as Location;
            }
        } catch (error) {
            window.location = "/checkout/" as unknown as Location;
        }
    } else if (window.shiprocketCheckoutEvents?.buyCart) {
        try {
            window.shiprocketCheckoutEvents.buyCart();
        } catch (error) {
            window.shiprocketCheckoutEvents = null;
            handleCustomCheckout();
            console.error("ShipRocket checkout failed", error);
        }
    } else if (window.openFlexyCheckout) {
        try {
            window.openFlexyCheckout();
        } catch (error) {
            window.openFlexyCheckout = null;
            handleCustomCheckout();
            console.error("FlexyPe checkout failed", error);
        }
    } else if (window.kefi_bundles.shop == "f90ec7-b7.myshopify.com") {
        if (document.getElementById("kefiCheckoutButton")) {
            const checkoutBtn = document.getElementById("CartDrawer-Checkout");
            if (checkoutBtn) {
                checkoutBtn.click();
            } else {
                window.location = "/checkout/" as unknown as Location;
            }
        } else {
            window.location = "/checkout/" as unknown as Location;
        }
    } else {
        window.location = "/checkout/" as unknown as Location;
        throw new Error("No custom checkout method available");
    }
}

export const moneyFormatter = new Intl.NumberFormat("en-US");

class CartNotificationDummy extends HTMLElement {
    constructor() {
        super();
    }

    open() {}

    close() {}

    renderContents() {}

    getSectionsToRender() {
        return [];
    }

    getSectionInnerHTML() {
        return "";
    }

    handleBodyClick() {}

    setActiveElement() {}
}

customElements.define("cart-notification-dummy", CartNotificationDummy);

export function isUpdatingCartLines(body: any): boolean {
    let parsedBody: Record<string, any> = {};
    try {
        if (typeof body === "string") {
            parsedBody = JSON.parse(body);
        } else if (body instanceof FormData || body instanceof URLSearchParams) {
            parsedBody = {};
            for (const [key, value] of body.entries()) {
                parsedBody[key] = value;
            }
        } else if (typeof body === "object" && body !== null) {
            parsedBody = body;
        } else {
            return false;
        }
    } catch (error) {
        console.error("Error parsing body:", error);
        return false;
    }

    const keys = Object.keys(parsedBody);
    const allowedKeys = ["updates"];

    return keys.every((key) => allowedKeys.includes(key)) && keys.length <= allowedKeys.length;
}

export function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
    let timeoutId: ReturnType<typeof setTimeout> | null;

    return function (this: any, ...args: Parameters<T>) {
        if (timeoutId) clearTimeout(timeoutId);

        timeoutId = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    } as T;
}

export const getModifiedImageUrl = (url: string, width: string = "200", height: string = "200") => {
    try {
        const parsedUrl = new URL(url);
        parsedUrl.searchParams.set("width", width);
        parsedUrl.searchParams.set("height", height);
        return parsedUrl?.toString();
    } catch (e) {
        console.error("Invalid URL:", url);
        return url;
    }
};

export const normalizeStepConditions = (step: StepsType | PlaceholderStepsType): StepConditionType[] => {
    const conditions = step?.step_conditions;

    // Case 1: Already an array and first object has a basis
    if (Array.isArray(conditions) && conditions.length > 0 && conditions[0]?.basis) {
        console?._log("normalizeStepConditions: Old But Key to New");

        return conditions;
    }

    // Case 2: Old format (single condition in keys)
    console?._log("normalizeStepConditions: Old to New");
    return [
        {
            // @ts-expect-error old keys
            basis: step.step_condition_basis || "items_count",
            // @ts-expect-error old keys

            limit: step.product_count_limit ? +step.product_count_limit : 10,
            // @ts-expect-error old keys
            type: step.step_condition_type || "max_product",
            // @ts-expect-error old keys
            msg: step.step_error_msg || "Select maximum {productCount} products",
        },
    ];
};

// Backfills any tier missing keys the same way BBTierDiscounts.tsx's "Add
// Tier" button (handleAddOption) builds a brand-new one — sequential from the
// previous tier — rather than from a static template. A generic
// defaults[0]-shaped template (see deepMergeWithDefaults) is wrong here: a
// saved bundle's 5th tier missing `maxQuantity` shouldn't get the *first*
// tier's maxQuantity, it should mirror its own `quantity` (the actual
// business rule — see handleDiscountInputChange's `quantity` case), and a
// wholly-absent tier beyond what was saved should be derived from the tier
// before it, exactly like adding one manually.
export const normalizeDiscountOptions = (options: BBTierOptions[]): BBTierOptions[] => {
    if (!Array.isArray(options) || options.length === 0) return options;

    const normalized: BBTierOptions[] = [];
    for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const prev = normalized[i - 1];
        const quantity = opt?.quantity ?? (prev ? +prev.quantity + 1 : 1);
        const discountType = opt?.discountType ?? prev?.discountType ?? "percentage";
        const discountValue =
            opt?.discountValue ??
            (prev
                ? prev.discountType === "percentage" && +prev.discountValue === 100
                    ? 100
                    : +prev.discountValue + 5
                : 10);

        normalized.push({
            ...opt,
            quantity,
            discountType,
            discountValue,
            // A tier's maxQuantity mirrors its own quantity unless the tier
            // explicitly set a wider range — never copied from another tier.
            maxQuantity: opt?.maxQuantity ?? quantity,
            addRange: opt?.addRange ?? true,
            ShowTierSuccessMessage: opt?.ShowTierSuccessMessage ?? true,
            TierSuccessMessage: opt?.TierSuccessMessage ?? "",
            showFinalSuccessMessage: opt?.showFinalSuccessMessage ?? true,
            finalSuccessMessage: opt?.finalSuccessMessage ?? "",
            selected_products: opt?.selected_products ?? [],
        });
    }
    return normalized;
};

export const normalizeFixedBoxes = (boxes: FixedBoxConfig[]): FixedBoxConfig[] => {
    if (!Array.isArray(boxes) || boxes.length === 0) return boxes;

    const normalized: FixedBoxConfig[] = [];
    const seenItemCounts = new Set<number>();
    for (let i = 0; i < boxes.length; i++) {
        const box = boxes[i];
        const prev = normalized[i - 1];
        let itemsCount = box?.itemsCount ?? (prev ? prev.itemsCount + 1 : 1);
        while (seenItemCounts.has(itemsCount)) {
            itemsCount++;
        }
        seenItemCounts.add(itemsCount);

        normalized.push({
            ...box,
            itemsCount,
            fixedPrice: box?.fixedPrice ?? (prev ? prev.fixedPrice + 10 : 50),
            boxName: box?.boxName?.trim() ? box.boxName : `Box of ${itemsCount}`,
        });
    }
    return normalized;
};

export const filterDuplicateStepConditions = (conditions: StepConditionType[]): StepConditionType[] => {
    const map = new Map<string, StepConditionType>();

    conditions.forEach((cond) => {
        const key = `${cond.basis}_${cond.type}`;
        const limit = Number(cond.limit);

        if (!map.has(key)) {
            map.set(key, { ...cond, limit });
        } else {
            const existing = map.get(key)!;

            switch (cond.type) {
                case "min_products":
                    existing.limit = Math.min(existing.limit, limit);
                    break;
                case "max_products":
                    existing.limit = Math.max(existing.limit, limit);
                    break;
                case "exact_products":
                    existing.limit = Math.max(existing.limit, limit);
                    break;
            }
        }
    });

    return Array.from(map.values());
};

export function showOverlayOutline(element: HTMLElement, options?: { isSelected?: boolean }) {
    if (!element) return;
    element.style.transition = "box-shadow 0.1s ease-in-out";
    element.style.boxShadow = options?.isSelected ? "0 0 0 2px rgba(42, 104, 192, 1)" : " 0 0 0 2px #458fffd5";
}

export function hideOverlayOutline(element: HTMLElement) {
    if (!element) return;
    element.style.transition = "box-shadow 0.1s ease-in-out";
    element.style.boxShadow = "";
}

export function initCartWatcher() {
    const shopConfig = CART_SHOPS_VARIANTS_INFO[window.kefi_bundles.shop];
    if (!shopConfig?.enableVariants) return;

    if (shopConfig.customCss) injectCSS(shopConfig.customCss);

    const triggerRender = async () => {
        try {
            const cartDataRes = await fetch("/cart.js?silent=" + Math.random());
            const cartData = await cartDataRes.json();
            handleCartItemVariantsRenderDebounced(cartData, shopConfig);
        } catch (error) {
            console.error("kefi: failed to fetch cart data in initCartWatcher trigger", error);
        }
    };

    // Listen to the cart event and call the debounced handler
    document.addEventListener(CART_CHANGE_FETCH_EVENT, (e: any) => {
        handleCartItemVariantsRenderDebounced(e.detail?.cartData, shopConfig);
    });

    if (shopConfig.observerSelector) {
        const targetNode = document.querySelector(shopConfig.observerSelector);
        if (targetNode) {
            const observer = new MutationObserver((mutations) => {
                let shouldTrigger = false;
                if (shopConfig.observeAttributes && shopConfig.observeAttributes.length > 0) {
                    shouldTrigger = mutations.some(
                        (mutation) =>
                            mutation.type === "attributes" &&
                            shopConfig.observeAttributes?.includes(mutation.attributeName || ""),
                    );
                } else {
                    shouldTrigger = true;
                }

                if (shouldTrigger) {
                    triggerRender();
                }
            });

            observer.observe(targetNode, {
                attributes: true,
                childList: true,
                subtree: true,
                attributeFilter: shopConfig.observeAttributes,
            });
        }
    }

    // Trigger on initial load
    setTimeout(() => {
        triggerRender();
    }, 2000);
}

export function removeFixedBreakingStyles(element: HTMLElement | null) {
    console.info("removeFixedBreakingStyles called on:", element);
    if (!element) return;
    try {
        let parent = element.parentElement;

        const breakProps = ["transform", "filter", "perspective", "contain", "will-change"];

        while (parent && parent !== document.body) {
            breakProps.forEach((prop: any) => {
                if (parent && getComputedStyle(parent).getPropertyValue(prop)) parent.style[prop] = "none";
            });

            parent = parent.parentElement;
        }
    } catch (error) {
        console.error("Error in removeFixedBreakingStyles:", error, element);
    }
}

export const getUsedFonts = (): string[] => {
    const bundles = window.kefi_bundles?.bundles_data?.matchedBundles ?? [];
    const cartFont = window.kefi_bundles?.cart_data?.cart_design?.cart_font_family;

    const bundleFonts = bundles
        .map((bundle: BundleConfig) => bundle?.bundles_design?.bundle_widget_font_family)
        .filter(Boolean);

    return cartFont ? [...bundleFonts, cartFont] : bundleFonts;
};

export const getDiscountValue = (
    discountType: "percentage" | "flat" | "fixed" | "free" | "none",
    discount_value: number,
    targets: any,
) => {
    let discountValue: any = null;

    switch (discountType) {
        case "percentage":
            discountValue = {
                percentage: { value: discount_value },
            };
            break;
        case "flat":
            discountValue = {
                fixedAmount: {
                    amount: discount_value,
                },
            };

            break;
        case "fixed":
            {
                let totalCost = 0;
                targets.forEach((target: any) => {
                    totalCost += +target.price;
                });

                const amountToReduce = totalCost - discount_value;

                discountValue = {
                    fixedAmount: {
                        amount: amountToReduce < 0 ? 0 : amountToReduce,
                    },
                };
            }
            break;
        case "free":
            discountValue = {
                percentage: {
                    value: 100,
                },
            };
            break;

        default:
            discountValue = {
                percentage: {
                    value: 0,
                },
            };
    }

    return discountValue;
};
